import type { CanFrame } from '../canlog/types';
import { extractSignalValue, type SignalExtractionConfig } from './bitExtraction';
import type { EventCorrelationEvidence, ScenarioTag } from './eventMarkers';

export const SIGNAL_CATEGORIES = [
  'wheel_speed',
  'rpm',
  'steering_angle',
  'brake',
  'throttle',
  'gear',
  'vehicle_speed',
  'tpms',
  'flag',
  'counter',
  'checksum',
  'other'
] as const;

export type SignalCategory = typeof SIGNAL_CATEGORIES[number];

export type DecodedPreviewPoint = {
  t: number;
  value: number;
};

export type DecodedPreview = {
  points: DecodedPreviewPoint[];
  frameCount: number;
  validSamples: number;
  invalidSamples: number;
  min: number | null;
  max: number | null;
  avg: number | null;
  first: number | null;
  last: number | null;
  uniqueCount: number;
  warnings: string[];
};

export type CandidateDraftInput = {
  canIdHex: string;
  signalName: string;
  category: SignalCategory;
  selectedByte: number;
  config: SignalExtractionConfig;
  unit: string;
  notes: string;
  preview: DecodedPreview;
  scenarioTag?: ScenarioTag;
  evidence?: EventCorrelationEvidence;
};

export type KnownValueScaleInput = {
  rawA: number;
  physicalA: number;
  rawB: number;
  physicalB: number;
};

export const SMARTCAN_DISCOVERY_METHODS = {
  candidateNaming: 'smartcan_candidate_name_v1',
  knownValueScaling: 'smartcan_known_value_affine_fit_v1',
  channelListExport: 'smartcan_channel_list_v1',
  dbcDraftExport: 'smartcan_dbc_draft_v1',
  previewBitExtraction: 'smartcan_preview_bit_extraction_v1',
  byteActivityScore: 'smartcan_byte_activity_score_v1',
  eventCorrelation: 'smartcan_event_variance_ratio_v1',
  counterHeuristic: 'smartcan_counter_wrap_heuristic_v1'
} as const;

function isNearlyConstant(uniqueCount: number, validSamples: number) {
  return validSamples > 3 && uniqueCount <= 1;
}

function looksLikeCounter(values: number[], length: number) {
  if (length > 8 || values.length < 6) return false;
  let sequential = 0;
  let wraps = 0;
  const modulo = 2 ** Math.min(length, 8);

  for (let index = 1; index < values.length; index += 1) {
    const previous = Math.trunc(values[index - 1]);
    const current = Math.trunc(values[index]);
    if (current === (previous + 1) % modulo) sequential += 1;
    if (current < previous) wraps += 1;
  }

  return sequential / (values.length - 1) > 0.72 || wraps > 1;
}

function downsample(points: DecodedPreviewPoint[], maxPoints = 800) {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % step === 0).slice(0, maxPoints);
}

export function buildDecodedPreview(frames: CanFrame[], config: SignalExtractionConfig, category: SignalCategory): DecodedPreview {
  const values: number[] = [];
  const points: DecodedPreviewPoint[] = [];
  let invalidSamples = 0;
  let min: number | null = null;
  let max: number | null = null;
  let sum = 0;
  let first: number | null = null;
  let last: number | null = null;

  for (const [index, frame] of frames.entries()) {
    const value = extractSignalValue(frame.data, config);
    if (value === null || !Number.isFinite(value)) {
      invalidSamples += 1;
      continue;
    }
    values.push(value);
    points.push({ t: Number.isFinite(frame.timestampMs) ? frame.timestampMs : index, value });
    min = min === null || value < min ? value : min;
    max = max === null || value > max ? value : max;
    sum += value;
    first = first ?? value;
    last = value;
  }

  const avg = values.length > 0 ? sum / values.length : null;
  const uniqueCount = new Set(values.map((value) => Number(value.toFixed(6)))).size;
  const warnings: string[] = [];

  if (isNearlyConstant(uniqueCount, values.length)) warnings.push('Mostly static value.');
  if (frames.length > 0 && invalidSamples / frames.length > 0.25) warnings.push('Many frames are too short or invalid for this range.');
  if (looksLikeCounter(values, config.length)) warnings.push(config.length === 8 ? 'Possible rolling counter.' : 'Possible counter/checksum behavior.');
  if (uniqueCount > Math.max(16, values.length * 0.8) && config.length <= 16) warnings.push('Very noisy candidate; validate before using.');
  if (category === 'vehicle_speed' && (min !== null && max !== null) && (min < -5 || max > 350)) warnings.push('Vehicle speed range looks unrealistic.');
  if (category === 'rpm' && (min !== null && max !== null) && (min < -50 || max > 12000)) warnings.push('RPM range looks unrealistic.');

  return {
    points: downsample(points),
    frameCount: frames.length,
    validSamples: values.length,
    invalidSamples,
    min,
    max,
    avg,
    first,
    last,
    uniqueCount,
    warnings
  };
}

function parseCanId(canIdHex: string) {
  const normalized = canIdHex.trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]{1,8}$/.test(normalized)) return 0;
  return Number.parseInt(normalized, 16);
}

function dbcIdentifier(value: string, fallback: string) {
  const cleaned = value
    .trim()
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  const next = cleaned || fallback;
  return /^[A-Za-z_]/.test(next) ? next : `_${next}`;
}

function slugIdentifier(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

export function suggestCandidateSignalName({
  canIdHex,
  category,
  config
}: Pick<CandidateDraftInput, 'canIdHex' | 'category' | 'config'>) {
  const categoryName = category === 'other' ? 'signal' : category;
  const byteIndex = Math.max(0, Math.min(7, Math.floor(config.startBit / 8)));
  const rangeLabel = config.length === 1
    ? `bit_${config.startBit}`
    : `b${byteIndex}_${config.length}bit`;
  const canIdLabel = canIdHex.replace(/^0x/i, '').toLowerCase();
  return slugIdentifier(`${categoryName}_${canIdLabel}_${rangeLabel}`) || 'candidate_signal';
}

export function calculateScaleOffsetFromKnownValues(input: KnownValueScaleInput) {
  const { rawA, physicalA, rawB, physicalB } = input;
  if (![rawA, physicalA, rawB, physicalB].every(Number.isFinite)) {
    return { ok: false as const, error: 'Enter numeric raw and physical values.' };
  }
  if (rawA === rawB) {
    return { ok: false as const, error: 'Raw values must be different.' };
  }

  const factor = (physicalB - physicalA) / (rawB - rawA);
  const offset = physicalA - rawA * factor;
  if (!Number.isFinite(factor) || !Number.isFinite(offset)) {
    return { ok: false as const, error: 'Could not calculate scale and offset.' };
  }

  return { ok: true as const, factor, offset };
}

export function buildCandidateJson(input: CandidateDraftInput) {
  return {
    can_id: input.canIdHex,
    signal_name: input.signalName,
    category: input.category,
    start_bit: input.config.startBit,
    length: input.config.length,
    endianness: input.config.endianness,
    signed: input.config.signed,
    factor: input.config.factor,
    offset: input.config.offset,
    unit: input.unit || undefined,
    selected_byte: input.selectedByte,
    source: 'user_candidate',
    status: 'candidate',
    needs_real_validation: true,
    methods: {
      naming: SMARTCAN_DISCOVERY_METHODS.candidateNaming,
      bit_extraction: SMARTCAN_DISCOVERY_METHODS.previewBitExtraction,
      scale_offset: SMARTCAN_DISCOVERY_METHODS.knownValueScaling
    },
    notes: input.notes || undefined,
    scenario_tag: input.scenarioTag,
    evidence: input.evidence,
    preview_stats: {
      min: input.preview.min,
      max: input.preview.max,
      avg: input.preview.avg,
      valid_samples: input.preview.validSamples,
      frame_count: input.preview.frameCount
    }
  };
}

export function buildCandidateChannelList(input: CandidateDraftInput) {
  const canId = parseCanId(input.canIdHex);
  const messageName = dbcIdentifier(`SMARTCAN_${input.canIdHex.replace(/^0x/i, '')}`, 'SMARTCAN_MESSAGE');
  const signalName = dbcIdentifier(input.signalName, suggestCandidateSignalName(input));
  return {
    schema: SMARTCAN_DISCOVERY_METHODS.channelListExport,
    generated_by: 'SmartCAN Analyzer',
    status: 'draft',
    validation_required: true,
    bit_numbering_note: 'SmartCAN preview extraction; validate bit numbering before production DBC use.',
    channels: [
      {
        name: messageName,
        can_id: input.canIdHex,
        can_id_decimal: canId,
        dlc: 8,
        source: 'user_candidate',
        signals: [
          {
            name: signalName,
            display_name: input.signalName,
            category: input.category,
            start_bit: input.config.startBit,
            length: input.config.length,
            endianness: input.config.endianness,
            signed: input.config.signed,
            factor: input.config.factor,
            offset: input.config.offset,
            unit: input.unit || '',
            min_observed: input.preview.min,
            max_observed: input.preview.max,
            selected_byte: input.selectedByte,
            needs_real_validation: true,
            evidence: input.evidence
          }
        ]
      }
    ]
  };
}

export function buildCandidateDbc(input: CandidateDraftInput) {
  const canId = parseCanId(input.canIdHex);
  const messageName = dbcIdentifier(`SMARTCAN_${input.canIdHex.replace(/^0x/i, '')}`, 'SMARTCAN_MESSAGE');
  const signalName = dbcIdentifier(input.signalName, suggestCandidateSignalName(input));
  const byteOrder = input.config.endianness === 'little' ? 1 : 0;
  const sign = input.config.signed ? '-' : '+';
  const min = input.preview.min ?? 0;
  const max = input.preview.max ?? 0;
  const unit = (input.unit || '').replace(/"/g, '');
  const comment = 'Draft exported from SmartCAN Analyzer. Validate preview bit numbering, scale, offset, and vehicle context before using as a production DBC.';

  return [
    'VERSION "SmartCAN candidate DBC draft"',
    '',
    'NS_ :',
    '\tNS_DESC_',
    '\tCM_',
    '\tBA_DEF_',
    '\tBA_',
    '\tVAL_',
    '',
    'BS_:',
    '',
    'BU_: Vector__XXX',
    '',
    `BO_ ${canId} ${messageName}: 8 Vector__XXX`,
    ` SG_ ${signalName} : ${input.config.startBit}|${input.config.length}@${byteOrder}${sign} (${input.config.factor},${input.config.offset}) [${min},${max}] "${unit}" Vector__XXX`,
    '',
    `CM_ BO_ ${canId} "${comment}";`,
    `CM_ SG_ ${canId} ${signalName} "Source: user_candidate; category: ${input.category}; method: ${SMARTCAN_DISCOVERY_METHODS.dbcDraftExport}.";`,
    ''
  ].join('\n');
}
